import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { planOutput } from '../utils/meetingAudioOutput';
import { createSquareCrop, wantsSquare } from '../utils/squareCamera';
import {
    createDenoiseProcessor, isMeetingDenoiseSupported, rememberMeetingDenoise, storedMeetingDenoise
} from '../utils/meetingDenoise';
import {
    DisconnectReason, LocalAudioTrack, LocalVideoTrack, Room, RoomEvent, Track, VideoPresets
} from 'livekit-client';

/**
 * One meeting's media, through the LiveKit media server.
 *
 * Deliberately separate from the chat call (context/CallContext.js): it has its
 * own connection and its own devices, so nothing here can reach a call and
 * nothing in a call can reach this. The server decides who may be in the room;
 * this only connects with the token it was given.
 *
 * Unlike calls, LiveKit opens the microphone, camera and screen itself.
 */

const DISCONNECT_MESSAGES = {
    [DisconnectReason.PARTICIPANT_REMOVED]: 'The host removed you from the meeting.',
    [DisconnectReason.ROOM_DELETED]: 'The meeting has ended.',
    [DisconnectReason.DUPLICATE_IDENTITY]: 'You joined this meeting from another window or device, so this one was closed.',
    [DisconnectReason.JOIN_FAILURE]: 'Could not connect to the meeting.',
    [DisconnectReason.SERVER_SHUTDOWN]: 'The meeting server restarted. Please rejoin.'
};

/**
 * Publish the microphone and camera the pre-join screen already opened, instead of
 * asking the browser for them a second time. Safari on iPhone asks the person
 * again on every new request; carrying the open stream across means one prompt.
 * Anything that cannot be carried over falls back to LiveKit opening it itself.
 */
const publishPreview = async (local, preview, { micOn, camOn }) => {
    const live = (track) => track && track.readyState === 'live';
    const [audio] = micOn ? preview.getAudioTracks() : [];
    const [video] = camOn ? preview.getVideoTracks() : [];
    const used = new Set();

    let micDone = false;
    if (live(audio)) {
        try {
            await local.publishTrack(new LocalAudioTrack(audio, undefined, false), { source: Track.Source.Microphone });
            used.add(audio);
            micDone = true;
        } catch { /* fall back below */ }
    }
    let camDone = false;
    if (live(video)) {
        try {
            await local.publishTrack(new LocalVideoTrack(video, undefined, false), { source: Track.Source.Camera });
            used.add(video);
            camDone = true;
        } catch { /* fall back below */ }
    }
    preview.getTracks().forEach((t) => { if (!used.has(t)) t.stop(); });
    return { micDone, camDone };
};

const ROUTE_LABEL = /speaker|earpiece|handset|headset|headphone|bluetooth|earbud|airpod/i;

const publishDefaults = {
    dtx: true,
    red: true,
    // Same trade as calls: 720p plus one lower layer to keep the server's CPU down.
    simulcast: true,
    videoSimulcastLayers: [VideoPresets.h360],
    videoEncoding: VideoPresets.h720.encoding,
    screenShareEncoding: { maxBitrate: 1_500_000, maxFramerate: 15 },
    degradationPreference: 'maintain-resolution'
};

const readName = (participant) => participant.name || participant.identity;

// The server puts a picture address in each person's token (see
// server/utils/meetingRoom.js); it is what their tile shows while their camera is off.
const readPhoto = (participant) => {
    try {
        const photo = JSON.parse(participant.metadata || '{}')?.photo;
        return typeof photo === 'string' ? photo : '';
    } catch {
        return '';
    }
};

/** A plain description of someone in the room, for the screen to draw. */
const describe = (participant, isLocal) => {
    const camera = participant.getTrackPublication(Track.Source.Camera);
    const screen = participant.getTrackPublication(Track.Source.ScreenShare);
    const mic = participant.getTrackPublication(Track.Source.Microphone);
    return {
        identity: participant.identity,
        name: readName(participant),
        photo: readPhoto(participant),
        isLocal,
        speaking: participant.isSpeaking,
        // 'excellent' | 'good' | 'poor' | 'lost' | 'unknown'
        quality: participant.connectionQuality,
        micOn: Boolean(mic) && !mic.isMuted,
        camOn: Boolean(camera?.track) && !camera.isMuted,
        screenOn: Boolean(screen?.track),
        cameraTrack: camera?.track || null,
        screenTrack: screen?.track || null
    };
};

export const useMeetingRoom = () => {
    const roomRef = useRef(null);
    const audioHost = useRef(null);
    const [, bump] = useReducer((n) => n + 1, 0);
    const [status, setStatus] = useState('idle'); // idle | connecting | connected | reconnecting | ended
    const [message, setMessage] = useState('');
    const [audioBlocked, setAudioBlocked] = useState(false);
    const [outputs, setOutputs] = useState([]);
    const [outputId, setOutputId] = useState('');
    // Android Chrome has no speaker list; it offers the phone's sound routes as inputs
    // (Speakerphone, Headset earpiece, Bluetooth headset), and choosing one moves the sound too.
    const [routes, setRoutes] = useState([]);
    // Noise cancellation on the microphone, as in calls: on unless switched off, and remembered.
    const denoiseSupported = useMemo(() => isMeetingDenoiseSupported(), []);
    const [denoiseOn, setDenoiseOn] = useState(storedMeetingDenoise);
    const denoiseRef = useRef(storedMeetingDenoise());
    const [routeId, setRouteId] = useState('');
    // What the speaker logic remembers between looks at the list of outputs.
    const knownOutputs = useRef(new Set());
    const userChoseOutput = useRef(false);
    const outputIdRef = useRef('');

    // Remote audio plays through hidden elements kept outside React's tree, so a
    // re-render can never restart someone's voice.
    const ensureAudioHost = () => {
        if (audioHost.current) return audioHost.current;
        const el = document.createElement('div');
        // Out of sight but still on the page. `display: none` is not the same:
        // some phone browsers do not play sound from an element that is not rendered.
        Object.assign(el.style, {
            position: 'fixed', left: '-9999px', top: '0', width: '1px', height: '1px',
            overflow: 'hidden', opacity: '0', pointerEvents: 'none'
        });
        document.body.appendChild(el);
        audioHost.current = el;
        return el;
    };

    const teardownAudio = () => {
        audioHost.current?.remove();
        audioHost.current = null;
    };

    const disconnect = useCallback(async (note = '') => {
        const room = roomRef.current;
        roomRef.current = null;
        if (room) {
            room.removeAllListeners();
            try { await room.disconnect(); } catch { /* already gone */ }
        }
        teardownAudio();
        setMessage(note);
        setStatus('ended');
    }, []);

    const connect = useCallback(async ({
        url, token, micOn = true, camOn = false, audioDeviceId, videoDeviceId, outputDeviceId, preview = null
    }) => {
        const releasePreview = () => preview?.getTracks().forEach((t) => t.stop());
        if (roomRef.current) { releasePreview(); return; }
        setStatus('connecting');
        setMessage('');
        // A fresh meeting starts with nothing remembered about speakers. Whatever
        // the pre-join screen chose is the person's choice, and counts as made by hand.
        knownOutputs.current = new Set();
        userChoseOutput.current = Boolean(outputDeviceId);
        outputIdRef.current = outputDeviceId || '';
        setOutputId(outputDeviceId || '');

        const room = new Room({
            adaptiveStream: true,
            dynacast: true,
            publishDefaults,
            audioCaptureDefaults: {
                echoCancellation: true, noiseSuppression: true, autoGainControl: true, deviceId: audioDeviceId
            },
            videoCaptureDefaults: { resolution: VideoPresets.h720.resolution, deviceId: videoDeviceId },
            audioOutput: outputDeviceId ? { deviceId: outputDeviceId } : undefined
        });
        roomRef.current = room;

        const refresh = () => { if (roomRef.current === room) bump(); };

        room
            .on(RoomEvent.ParticipantConnected, refresh)
            .on(RoomEvent.ParticipantDisconnected, refresh)
            .on(RoomEvent.TrackPublished, refresh)
            .on(RoomEvent.TrackUnpublished, refresh)
            .on(RoomEvent.TrackMuted, refresh)
            .on(RoomEvent.TrackUnmuted, refresh)
            .on(RoomEvent.LocalTrackPublished, refresh)
            .on(RoomEvent.LocalTrackPublished, (publication) => {
                // Clean the microphone (the same denoiser the calls use).
                const mic = publication.track;
                if (publication.source === Track.Source.Microphone && mic?.setProcessor && denoiseSupported
                    && denoiseRef.current && !mic.getProcessor?.()) {
                    mic.setProcessor(createDenoiseProcessor()).catch(() => { /* the plain microphone carries on */ });
                }
                // A phone's tall picture goes out as a square, not stretched into a wide tile.
                const track = publication.track;
                if (publication.source !== Track.Source.Camera || !track?.setProcessor || track.getProcessor?.()) return;
                if (!wantsSquare(track.mediaStreamTrack?.getSettings?.())) return;
                track.setProcessor(createSquareCrop()).then(refresh).catch(() => { /* the plain picture still works */ });
            })
            .on(RoomEvent.LocalTrackUnpublished, refresh)
            .on(RoomEvent.ActiveSpeakersChanged, refresh)
            .on(RoomEvent.ConnectionQualityChanged, refresh)
            .on(RoomEvent.ParticipantNameChanged, refresh)
            .on(RoomEvent.TrackSubscribed, (track) => {
                if (track.kind === Track.Kind.Audio) ensureAudioHost().appendChild(track.attach());
                refresh();
            })
            .on(RoomEvent.TrackUnsubscribed, (track) => {
                track.detach().forEach((el) => el.remove());
                refresh();
            })
            .on(RoomEvent.AudioPlaybackStatusChanged, () => setAudioBlocked(!room.canPlaybackAudio))
            .on(RoomEvent.Reconnecting, () => { if (roomRef.current === room) setStatus('reconnecting'); })
            .on(RoomEvent.Reconnected, () => { if (roomRef.current === room) setStatus('connected'); })
            .on(RoomEvent.Disconnected, (reason) => {
                // Ours (leave) was already handled; anything else is news.
                if (roomRef.current !== room) return;
                roomRef.current = null;
                teardownAudio();
                setMessage(DISCONNECT_MESSAGES[reason] || 'You were disconnected from the meeting.');
                setStatus('ended');
            });

        try {
            await room.connect(url, token);
            // The Join tap already unlocked sound (utils/unlockAudio.js). Try to start it;
            // and whatever the browser says, believe the room - if it still cannot play,
            // the person is shown a button to tap (audioBlocked).
            await room.startAudio().catch(() => { });
            setAudioBlocked(!room.canPlaybackAudio);
            setStatus('connected');
            const carried = preview ? await publishPreview(room.localParticipant, preview, { micOn, camOn }) : {};
            if (!carried.micDone) await room.localParticipant.setMicrophoneEnabled(Boolean(micOn)).catch(() => { /* no mic - they can still listen */ });
            if (camOn && !carried.camDone) await room.localParticipant.setCameraEnabled(true).catch(() => { });
            refresh();
        } catch (err) {
            releasePreview();
            if (roomRef.current === room) {
                roomRef.current = null;
                room.removeAllListeners();
                teardownAudio();
            }
            setMessage('Could not connect to the meeting. Check your connection and try again.');
            setStatus('ended');
        }
    }, [denoiseSupported]);

    // Closing the tab or leaving the page always lets go of the devices.
    useEffect(() => () => {
        const room = roomRef.current;
        roomRef.current = null;
        if (room) {
            room.removeAllListeners();
            room.disconnect().catch(() => { });
        }
        teardownAudio();
    }, []);

    const room = roomRef.current;
    const local = room ? room.localParticipant : null;

    const toggle = async (action) => {
        try { await action(); } catch (err) { setMessage(err?.message || 'That did not work.'); }
        bump();
    };

    const outputSupported = typeof HTMLMediaElement !== 'undefined'
        && typeof HTMLMediaElement.prototype.setSinkId === 'function';

    // Make an output the one in use. 'default' hands the choice back to the browser.
    const applyOutput = useCallback(async (deviceId) => {
        await roomRef.current?.switchActiveDevice('audiooutput', deviceId);
        const stored = deviceId === 'default' ? '' : deviceId;
        outputIdRef.current = stored;
        setOutputId(stored);
    }, []);

    const refreshOutputs = useCallback(async () => {
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            const list = all.filter((d) => d.kind === 'audiooutput').map((d, i) => ({
                deviceId: d.deviceId,
                label: d.label || (d.deviceId === 'default' ? 'Default' : `Speaker ${i + 1}`)
            }));
            setOutputs(list);
            // Only when the browser gives no real speaker list of its own.
            setRoutes(list.length > 1 ? [] : all.filter((d) => d.kind === 'audioinput' && ROUTE_LABEL.test(d.label))
                .map((d) => ({ deviceId: d.deviceId, label: d.label })));

            // Bluetooth earbuds or a headset take the sound whenever one is connected;
            // the person can still pick the speaker by hand (utils/meetingAudioOutput.js).
            const plan = planOutput({
                outputs: list,
                knownIds: knownOutputs.current,
                currentId: outputIdRef.current,
                userChose: userChoseOutput.current
            });
            knownOutputs.current = new Set(list.map((o) => o.deviceId));
            if (outputSupported && roomRef.current && plan.switchTo) {
                try {
                    await applyOutput(plan.switchTo);
                    if (plan.resetChoice) userChoseOutput.current = false;
                } catch { /* the phone would not hand it over; the sound stays where it was */ }
            }
        } catch { /* no list: the picker just says so */ }
    }, [outputSupported, applyOutput]);

    // Keep the list current while in the meeting: a headset or Bluetooth device
    // that connects (or leaves) mid-meeting appears (or goes) by itself.
    useEffect(() => {
        if (status !== 'connected' || !navigator.mediaDevices) return undefined;
        refreshOutputs();
        navigator.mediaDevices.addEventListener?.('devicechange', refreshOutputs);
        return () => navigator.mediaDevices.removeEventListener?.('devicechange', refreshOutputs);
    }, [status, refreshOutputs]);

    // Switch noise cancellation on or off for the microphone that is already live, and for next time.
    const toggleDenoise = useCallback(async () => {
        const next = !denoiseRef.current;
        denoiseRef.current = next;
        setDenoiseOn(next);
        rememberMeetingDenoise(next);
        const mic = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
        if (!mic) return;
        try {
            if (next && !mic.getProcessor?.()) await mic.setProcessor(createDenoiseProcessor());
            if (!next && mic.getProcessor?.()) await mic.stopProcessor();
        } catch { /* the microphone keeps working as it was */ }
    }, []);

    const switchRoute = useCallback(async (deviceId) => {
        try {
            await roomRef.current?.switchActiveDevice('audioinput', deviceId);
            setRouteId(deviceId);
        } catch {
            setMessage('That sound output could not be used. Your phone may be holding it for something else.');
        }
    }, []);

    // Picked by hand: respected until a different headset connects or this one goes.
    const switchOutput = useCallback(async (deviceId) => {
        userChoseOutput.current = true;
        try {
            await applyOutput(deviceId);
        } catch {
            setMessage('That speaker could not be used. Your phone may be holding it for something else.');
        }
    }, [applyOutput]);

    const participants = room
        ? [describe(room.localParticipant, true), ...[...room.remoteParticipants.values()].map((p) => describe(p, false))]
        : [];

    return {
        status,
        message,
        participants,
        micOn: Boolean(local?.isMicrophoneEnabled),
        camOn: Boolean(local?.isCameraEnabled),
        sharing: Boolean(local?.isScreenShareEnabled),
        audioBlocked,
        canShareScreen: typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia),
        connect,
        disconnect,
        toggleMic: () => toggle(() => local.setMicrophoneEnabled(!local.isMicrophoneEnabled)),
        toggleCamera: () => toggle(() => local.setCameraEnabled(!local.isCameraEnabled)),
        toggleScreenShare: () => toggle(() => local.setScreenShareEnabled(!local.isScreenShareEnabled, { audio: true })),
        // iPhone Safari cannot choose an output itself, but can show Apple's own picker
        // (speaker, AirPods, AirPlay) for the meeting's sound. Needs a tap.
        canPickRoute: typeof HTMLMediaElement !== 'undefined'
            && typeof HTMLMediaElement.prototype.webkitShowPlaybackTargetPicker === 'function',
        pickRoute: () => {
            const el = audioHost.current?.querySelector('audio');
            try { el?.webkitShowPlaybackTargetPicker(); } catch { /* nothing to pick for */ }
        },
        // Called from a tap: start every voice that the browser held back.
        enableAudio: async () => {
            await room?.startAudio().catch(() => { });
            audioHost.current?.querySelectorAll('audio').forEach((el) => { el.play().catch(() => { }); });
            setAudioBlocked(room ? !room.canPlaybackAudio : false);
        },
        // Where the meeting's sound comes out (speaker, headset, Bluetooth...).
        // `outputSupported` is false where the browser does not let a page choose -
        // iPhone Safari - and the person is told how their phone decides instead.
        outputSupported,
        outputs,
        outputId,
        routes,
        routeId,
        switchRoute,
        denoiseSupported,
        denoiseOn,
        toggleDenoise,
        switchOutput
    };
};

export default useMeetingRoom;
